import React, { Suspense, useDeferredValue, useRef } from 'react';
import { PageLoader } from 'shared/components/ui/page-loader';
import {
  CalendarViewOptions,
  UpcomingViewOptions,
} from 'shared/types/upcoming-view-options';

import { useCalendarViewControls } from '../../hooks/use-calendar-view-controls';
import { PageTopBar } from '../page-top-bar';
import { UpcomingGroupedList } from '../upcoming-grouped-list';
import { CalendarView } from './calendar-view';
import * as Styled from './main.style';

export type MainProps = {
  onToggleSidePanel: () => void;
  sidPanelOpen?: boolean;
  onChangeView: (view: UpcomingViewOptions) => void;
  selectedView: UpcomingViewOptions;
};

export const Main: React.FC<MainProps> = ({
  sidPanelOpen,
  onToggleSidePanel,
  selectedView,
  onChangeView,
}) => {
  const deferredSelectedView = useDeferredValue(selectedView);
  const {
    referenceDate: calendarReferenceDate,
    onChangeView: onChangeCalendarView,
    selectedView: selectedCalendarView,
    onToday,
    onBigNext,
    onBigPrev,
    scrollTime,
  } = useCalendarViewControls(true);

  const isTransitioningView = selectedView !== deferredSelectedView;
  const scrollElement = useRef<HTMLDivElement>(null);

  return (
    <>
      <PageTopBar
        onPrev={onBigPrev}
        onToday={onToday}
        onNext={onBigNext}
        sidePanelOpen={sidPanelOpen}
        onToggleSidePanel={onToggleSidePanel}
        selectedCalendarView={selectedCalendarView ?? CalendarViewOptions.Week}
        changeCalendarView={onChangeCalendarView}
        changeSelectedView={onChangeView}
        selectedView={selectedView}
        referenceDate={
          selectedView === UpcomingViewOptions.Calendar
            ? calendarReferenceDate
            : undefined
        }
      />
      <Styled.Content ref={scrollElement}>
        {isTransitioningView && <PageLoader />}
        {deferredSelectedView === UpcomingViewOptions.List && (
          <UpcomingGroupedList scrollElement={scrollElement} />
        )}
        {deferredSelectedView === UpcomingViewOptions.Calendar &&
          selectedCalendarView && (
            <Suspense fallback={<PageLoader />}>
              <CalendarView
                view={selectedCalendarView}
                referenceDate={calendarReferenceDate}
                scrollTime={scrollTime}
              />
            </Suspense>
          )}
      </Styled.Content>
    </>
  );
};
